import type { AxiosInstance } from 'axios';
import { AxiosError } from 'axios';
import type { Lab } from '../../types/Lab';
import type { Model } from '../../types/Model';

// Global constants for the module
const LAB_RETRY_DELAYS = [500, 500, 1000];
const LAB_MAX_RETRIES = 3;

export interface labRequest {
  name?: string;
  picture?: string;
  description?: string;
  handle?: string;
  flair?: string;
  status?: 'active' | 'archived';
  visibility?: 'private' | 'public' | 'unlisted';
}

interface PaginatedLabResponse {
  items: Lab[];
  cursor: string | null;
}

// Admin labs query parameters
interface AdminLabsQuery {
  search?: string;
  status?: 'active' | 'archived' | 'all';
  pageSize?: number;
  pageNumber?: number;
}

// Admin labs response with pagination
interface AdminLabsResponse {
  items: Lab[];
  pagination: {
    totalItems: number;
    totalPages: number;
    pageNumber: number;
    pageSize: number;
  };
}

interface FollowedLabsResponse {
  items: Lab[];
  cursor: string | null;
}

export const LabService = (modelApi: AxiosInstance) => ({
  // Model API operations
  getLabs: async (searchTerm?: string) => {
    const url = searchTerm
      ? `/labs?search=${encodeURIComponent(searchTerm)}`
      : '/labs';
    const response = await modelApi.get(url);
    return response.data;
  },

  // Admin endpoint - get labs with pagination and filtering
  getAdminLabs: async (query?: AdminLabsQuery): Promise<AdminLabsResponse> => {
    try {
      const params: Record<string, string> = {};

      if (query?.search) params.search = query.search;
      if (query?.status) params.status = query.status;
      if (query?.pageSize !== undefined) params.pageSize = query.pageSize.toString();
      if (query?.pageNumber !== undefined) params.pageNumber = query.pageNumber.toString();

      const response = await modelApi.get<AdminLabsResponse>('/admin/labs', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting admin labs:', error);
      return {
        items: [],
        pagination: {
          totalItems: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize: 20
        }
      };
    }
  },

  // Admin endpoint - add user to lab
  addLabUser: async (labId: string, userId: string): Promise<{ success: boolean }> => {
    try {
      const response = await modelApi.post<{ success: boolean }>(`/admin/labs/${labId}/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error adding user to lab:', error);
      return { success: false };
    }
  },

  // Admin endpoint - remove user from lab
  removeLabUser: async (labId: string, userId: string): Promise<{ success: boolean }> => {
    try {
      const response = await modelApi.delete<{ success: boolean }>(`/admin/labs/${labId}/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing user from lab:', error);
      return { success: false };
    }
  },

  // Get labs for a user
  getProfileLabs: async (userId: string) => {
    try {
      const response = await modelApi.get<PaginatedLabResponse>(`/profiles/${userId}/labs`);
      return response.data.items;
    } catch (error) {
      console.error('Error getting profile labs', error);
      return [];
    }
  },

  //Create new lab
  createLab: async (data: labRequest) => {
    try {
      const response = await modelApi.post<Lab>('/labs', data);
      // verify lab is in opensearch
      for (let attempt = 0; attempt <= LAB_MAX_RETRIES; attempt++) {
        try {
          const labResponse = await modelApi.get<Lab>(`/labs/${response.data.id}`);
          // If we get here, the lab exists
          return labResponse.data;
        } catch (error) {
          // If we get a 404, the lab doesn't exist yet
          if (error instanceof AxiosError && error.response?.status === 404) {
            if (attempt < LAB_MAX_RETRIES) {
              console.log(`Lab not found, retrying in ${LAB_RETRY_DELAYS[attempt]}ms (attempt ${attempt + 1}/${LAB_MAX_RETRIES})`);
              await new Promise(resolve => setTimeout(resolve, LAB_RETRY_DELAYS[attempt]));
            } else {
              console.log('Lab not found after all retries');
              return null;
            }
          } else {
            // For other errors, throw them
            throw error;
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error creating lab:', error);
      return null;
    }
  },

  // Get public lab by handle
  getLab: async (handle: string) => {
    const response = await modelApi.get<Lab>(`/labs/${handle}`);
    return response.data;
  },

  // Update lab
  updateLab: async (id: string, data: labRequest) => {
    try {
      const response = await modelApi.put<Lab>(`/labs/${id}`, data);
      // verify lab is in opensearch and matches update response
      for (let attempt = 0; attempt <= LAB_MAX_RETRIES; attempt++) {
        try {
          const labResponse = await modelApi.get<Lab>(`/labs/${id}`);
          // Check if the specific fields match
          const fieldsMatch = 
            labResponse.data.name === response.data.name &&
            labResponse.data.handle === response.data.handle &&
            labResponse.data.description === response.data.description &&
            labResponse.data.picture === response.data.picture;
          
          if (fieldsMatch) {
            return response.data;
          }
          // If data doesn't match, wait and retry
          if (attempt < LAB_MAX_RETRIES) {
            console.log(`Lab data not in sync, retrying in ${LAB_RETRY_DELAYS[attempt]}ms (attempt ${attempt + 1}/${LAB_MAX_RETRIES})`);
            await new Promise(resolve => setTimeout(resolve, LAB_RETRY_DELAYS[attempt]));
          } else {
            console.log('Lab data not in sync after all retries');
            return response.data; // Return original response if verification times out
          }
        } catch (error) {
          // If we get a 404 or other error, wait and retry
          if (attempt < LAB_MAX_RETRIES) {
            console.log(`Error verifying lab update, retrying in ${LAB_RETRY_DELAYS[attempt]}ms (attempt ${attempt + 1}/${LAB_MAX_RETRIES})`);
            await new Promise(resolve => setTimeout(resolve, LAB_RETRY_DELAYS[attempt]));
          } else {
            throw error;
          }
        }
      }
      return response.data;
    } catch (error) {
      console.error('Error updating lab:', error);
      return null;
    }
  },

  // check if a handle is available
  checkHandleAvailability: async (handle: string) => {
    const response = await modelApi.get<{available: boolean}>(`/handles/check?handle=${handle}`);
    return response.data.available;
  },

  // Identity API operations
  followLab: async (id: string) => {
    try {
      await modelApi.post<Lab>(`/labs/${id}/followers`);
      return true;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400 && error.response?.data?.message === 'Already following this lab') {
        return true;
      }
      console.error('Error following lab', error);
      return false;
    }
  },

  // Unfollow a lab
  unfollowLab: async (id: string) => {
    const response = await modelApi.delete<Lab>(`/labs/${id}/followers`);
    return response.data;
  },

  // List followed labs
  listFollowedLabs: async (userId: string) => {
    try {
      const response = await modelApi.get<FollowedLabsResponse>(`/profiles/${userId}/following/labs`);
      return response.data.items;
    } catch (error) {
      console.error('Error listing followed labs', error);
      return [];
    }
  },

  // Get latest model with a version not in published state
  getLabModelsUnpublished: async (id: string) => {
    try {
      const response = await modelApi.get<Model[]>(`/labs/${id}/models/unpublished`);
      return response.data;
    } catch (error) {
      console.error('Error getting latest model with unpublished version', error);
      return [];
    }
  }
}); 